import React from "react";

/**
 * Cozy/autumn sticker-style accents used across the whole site.
 *
 * Every "object" is a real transparent SVG cutout served from the Twemoji CDN
 * so nothing has a background box around it. Scatter presets place items in
 * the OUTER margins of a section (never on top of centred content) and use
 * different rotations, sizes and float animations to feel like hand-placed
 * stickers.
 *
 * Component is purely decorative — `pointer-events-none` + `aria-hidden`.
 */

const TW = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg";

// Named cutouts — thematic match to the reference sticker sheets.
export const CUTOUTS = {
    // Cozy pantry
    pumpkin: `${TW}/1f383.svg`,
    pie: `${TW}/1f967.svg`,
    pear: `${TW}/1f350.svg`,
    apple: `${TW}/1f34e.svg`,
    banana: `${TW}/1f34c.svg`,
    orange: `${TW}/1f34a.svg`,
    watermelon: `${TW}/1f349.svg`,
    pineapple: `${TW}/1f34d.svg`,
    cherries: `${TW}/1f352.svg`,
    corn: `${TW}/1f33d.svg`,
    honey: `${TW}/1f36f.svg`,
    milk: `${TW}/1f95b.svg`,
    // Leaves & botanicals
    maple: `${TW}/1f341.svg`,
    leaf: `${TW}/1f342.svg`,
    herb: `${TW}/1f33f.svg`,
    potted: `${TW}/1fab4.svg`,
    mushroom: `${TW}/1f344.svg`,
    sunflower: `${TW}/1f33b.svg`,
    // Warm drinks & sweets
    teapot: `${TW}/1fad6.svg`,
    tea_mug: `${TW}/1f375.svg`,
    coffee: `${TW}/2615.svg`,
    cake: `${TW}/1f370.svg`,
    cupcake: `${TW}/1f9c1.svg`,
    popcorn: `${TW}/1f37f.svg`,
    croissant: `${TW}/1f950.svg`,
    candy: `${TW}/1f36c.svg`,
    // Weather / clothing
    scarf: `${TW}/1f9e3.svg`,
    boot: `${TW}/1f97e.svg`,
    umbrella: `${TW}/2614.svg`,
    // Little friends
    cat: `${TW}/1f408.svg`,
    rabbit: `${TW}/1f407.svg`,
    hedgehog: `${TW}/1f994.svg`,
    panda: `${TW}/1f43c.svg`,
    bird: `${TW}/1f426.svg`,
    // Decorative accents
    rainbow: `${TW}/1f308.svg`,
    heart: `${TW}/1f497.svg`,
    star: `${TW}/2b50.svg`,
    sparkle: `${TW}/2728.svg`,
    book: `${TW}/1f4d6.svg`,
    candle: `${TW}/1f56f.svg`,
};

// Preset scatters — all positions sit in the OUTER edges of a section so they
// never overlap centred UI/text. Sizes stay small (40-120px) to feel like
// scattered stickers rather than illustrations.
const PRESETS = {
    // Home hero — autumn corners + a couple of floats around the gallery.
    hero: [
        { key: "pumpkin",   style: { top: 20,  left: -30 }, size: 90, rotate: -14, anim: "animate-float-slow" },
        { key: "maple",     style: { top: 4,   right: 40 }, size: 70, rotate: 12,  anim: "animate-float" },
        { key: "cherries",  style: { bottom: 30, left: 20 }, size: 74, rotate: -8, anim: "animate-float-slower" },
        { key: "sparkle",   style: { bottom: 90, left: -18 }, size: 44, rotate: 0, anim: "animate-float" },
        { key: "rainbow",   style: { top: 200, left: 6 }, size: 74, rotate: -6, anim: "animate-float-slower" },
        { key: "heart",     style: { top: 320, right: -14 }, size: 46, rotate: 10, anim: "animate-float" },
    ],
    // How-it-works — kitchen/craft motifs in the edges.
    craft: [
        { key: "teapot",    style: { top: 30,  left: -32 }, size: 96, rotate: -6, anim: "animate-float-slower" },
        { key: "pie",       style: { top: 60,  right: -20 }, size: 80, rotate: 10, anim: "animate-float" },
        { key: "leaf",      style: { bottom: 20, left: 40 }, size: 60, rotate: -18, anim: "animate-float-slow" },
        { key: "coffee",    style: { bottom: 40, right: 60 }, size: 66, rotate: 12, anim: "animate-float-slow" },
        { key: "mushroom",  style: { top: 200, right: 20 }, size: 60, rotate: -10, anim: "animate-float-slower" },
    ],
    // Testimonials — warm sweet items in the top + bottom corners.
    sweet: [
        { key: "tea_mug",   style: { top: -18, left: 20 }, size: 84, rotate: -10, anim: "animate-float-slow" },
        { key: "apple",     style: { top: -14, right: 40 }, size: 72, rotate: 8, anim: "animate-float" },
        { key: "star",      style: { bottom: -18, right: 20 }, size: 50, rotate: 0, anim: "animate-float-slower" },
        { key: "cupcake",   style: { bottom: -20, left: 60 }, size: 70, rotate: -6, anim: "animate-float" },
    ],
    // Founders hero — cat + scarf + sunflower.
    founders: [
        { key: "cat",       style: { top: 20,  right: -10 }, size: 110, rotate: 6, anim: "animate-float-slow" },
        { key: "scarf",     style: { bottom: 20, left: -20 }, size: 90, rotate: -12, anim: "animate-float" },
        { key: "sparkle",   style: { top: 40,  left: 30 }, size: 40, rotate: 0, anim: "animate-float-slower" },
        { key: "sunflower", style: { bottom: 80, right: 30 }, size: 74, rotate: 8, anim: "animate-float" },
        { key: "book",      style: { top: 200, left: -20 }, size: 66, rotate: -8, anim: "animate-float-slow" },
    ],
    // Contact — umbrella + boot + orange in outer corners.
    contact: [
        { key: "umbrella",  style: { top: 30,  right: -20 }, size: 96, rotate: 10, anim: "animate-float-slow" },
        { key: "boot",      style: { bottom: 40, left: -22 }, size: 90, rotate: -8, anim: "animate-float" },
        { key: "cherries",  style: { top: 100, left: -14 }, size: 60, rotate: -12, anim: "animate-float-slower" },
        { key: "orange",    style: { bottom: 100, right: -14 }, size: 60, rotate: 8, anim: "animate-float-slower" },
        { key: "candle",    style: { top: 240, right: 20 }, size: 54, rotate: 0, anim: "animate-float-slow" },
    ],
    // Shop grid — light scatter around the perimeter.
    shop: [
        { key: "pear",      style: { top: 20,  left: -14 }, size: 72, rotate: -10, anim: "animate-float-slow" },
        { key: "banana",    style: { top: 40,  right: -20 }, size: 74, rotate: 12, anim: "animate-float" },
        { key: "sparkle",   style: { bottom: 60, right: 6 }, size: 44, rotate: 0, anim: "animate-float-slower" },
        { key: "leaf",      style: { bottom: 80, left: 20 }, size: 60, rotate: -8, anim: "animate-float" },
    ],
    // Gallery — playful sweet + cute animal motifs.
    gallery: [
        { key: "rainbow",   style: { top: 10, right: -14 }, size: 90, rotate: 10, anim: "animate-float-slow" },
        { key: "rabbit",    style: { top: 200, left: -22 }, size: 78, rotate: -6, anim: "animate-float" },
        { key: "cupcake",   style: { bottom: 40, right: 20 }, size: 66, rotate: 8, anim: "animate-float-slower" },
        { key: "star",      style: { bottom: 200, left: 10 }, size: 50, rotate: -12, anim: "animate-float" },
    ],
    // Reviews — hearts, sparkles, coffee vibe.
    reviews: [
        { key: "heart",     style: { top: 40, left: -18 }, size: 70, rotate: -10, anim: "animate-float-slow" },
        { key: "coffee",    style: { top: 20, right: -14 }, size: 78, rotate: 8, anim: "animate-float" },
        { key: "sparkle",   style: { bottom: 60, right: 40 }, size: 46, rotate: 0, anim: "animate-float-slower" },
        { key: "popcorn",   style: { bottom: 80, left: 20 }, size: 74, rotate: -6, anim: "animate-float" },
    ],
    // FAQs — books, herbs, calming plants.
    faqs: [
        { key: "book",      style: { top: 30, left: -20 }, size: 74, rotate: -8, anim: "animate-float-slow" },
        { key: "herb",      style: { top: 20, right: -16 }, size: 68, rotate: 10, anim: "animate-float" },
        { key: "potted",    style: { bottom: 30, left: 10 }, size: 82, rotate: -4, anim: "animate-float-slower" },
        { key: "sparkle",   style: { bottom: 60, right: 20 }, size: 44, rotate: 0, anim: "animate-float" },
    ],
    // Track page — umbrella + boot + honey.
    track: [
        { key: "umbrella",  style: { top: 20, right: -14 }, size: 84, rotate: 8, anim: "animate-float-slow" },
        { key: "boot",      style: { bottom: 40, left: -14 }, size: 80, rotate: -6, anim: "animate-float" },
        { key: "honey",     style: { top: 200, left: 20 }, size: 60, rotate: -10, anim: "animate-float-slower" },
        { key: "sparkle",   style: { bottom: 80, right: 30 }, size: 44, rotate: 0, anim: "animate-float" },
    ],
    // How It Works dedicated page.
    howitworks: [
        { key: "croissant", style: { top: 30, left: -20 }, size: 80, rotate: -8, anim: "animate-float-slow" },
        { key: "milk",      style: { top: 60, right: -14 }, size: 74, rotate: 8, anim: "animate-float" },
        { key: "hedgehog",  style: { bottom: 40, left: 30 }, size: 84, rotate: -4, anim: "animate-float-slower" },
        { key: "candy",     style: { bottom: 100, right: 40 }, size: 56, rotate: 12, anim: "animate-float" },
    ],
    // Single accent for cards or small headers.
    corner: [
        { key: "sparkle",   style: { top: 10, right: 10 }, size: 42, rotate: 0 },
    ],
};

const Sticker = ({ src, style, size = 80, rotate = 0, testId, anim }) => (
    <img
        src={src}
        alt=""
        loading="lazy"
        aria-hidden="true"
        data-testid={testId}
        className={`absolute select-none pointer-events-none ${anim || ""}`}
        style={{
            width: size,
            height: size,
            objectFit: "contain",
            transform: `rotate(${rotate}deg)`,
            filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.12))",
            ...style,
        }}
    />
);

const SectionAccents = ({ variant = "hero", className = "" }) => {
    const items = PRESETS[variant] || [];
    if (!items.length) return null;
    return (
        <div
            className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
            data-testid={`accents-${variant}`}
            aria-hidden="true"
        >
            {items.map((it, i) => (
                <Sticker
                    key={i}
                    src={CUTOUTS[it.key]}
                    style={it.style}
                    size={it.size}
                    rotate={it.rotate}
                    anim={it.anim}
                    testId={`accents-${variant}-cutout-${i}`}
                />
            ))}
        </div>
    );
};

export default SectionAccents;
